/* eslint-disable */
import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import LayerHelper from '@/util/LayerHelper';
import { SAVE_QUERY_BUTTON } from '@/constants/test-ids';
import {
  ACTION_CLICK_TABLE_HEADER,
  ACTION_DISABLE,
  ACTION_DRILLDOWN,
  ACTION_SAVE_QUERY,
  CREATE_CHART,
  SUMMARIZE,
} from '@/components/HighlightFeature';
import {
  getChartingOptions,
  getQueryBuilderSteps,
  getRowsErrorMessage,
  getSelectedLayerId,
  getShowChartingOptions,
  getUserActions,
} from '@/store-selectors';
import { showOnboarding } from '@/util/helpers/onboarding-helpers';
import {
  Button,
  Dropdown,
  TrevorIcon,
  useDropdown,
} from '@trevor/ui-components';
import { selectMetricsDimensionConfiguration } from '@/features/queryMetricsDimensionsConfig/metricConfigSlice';
import { SaveQuerySuggestion } from './SaveQuerySuggestion';
import { ButtonText } from './ButtonText';
import {
  GET_SAVED_QUERY_CACHED_ROWS,
  GET_SAVED_QUERY_COLUMNS,
  queryBuilderSavedQueriesApi,
  useGetSavedQuery,
  useUpdateSavedQueryMutation,
} from '@/features/query-builder/savedQueriesApiSlice';
import { useDataPresentationParam, useDatasourceId } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useModal } from '@/features/modals/modalSlice';
import { SaveQueryModal } from './SaveQueryModal';
import { SavedQueryNextSteps } from '@/views/QueryBuilder/subcomponents/SaveButton/SavedQueryNextSteps';
import { useAppDispatch } from '@/store/hooks';
import { reset } from '@/features/query-builder-steps-undo-redo';
import { selectIsLoadingRows } from '@/features/query-builder/queryBuilderSlice';
import { latestAdHocPromises } from '@/features/query-builder/latestAdHocPromises';

export const ONBOARDING_SAVE_QUERY_REQUIRED_ACTION = [
  SUMMARIZE,
  ACTION_DRILLDOWN,
  ACTION_DISABLE,
  CREATE_CHART,
  ACTION_CLICK_TABLE_HEADER,
] as const;

const SaveOptionsContainer = styled(Dropdown.Content)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-s);
  z-index: 2;
  padding: var(--spacing-s);
  border-radius: var(--radius-8);
  box-shadow: var(--elevation-medium);
  background: var(--color-interface-neutral-light);
  min-width: 185px;
`;

const SaveOption = styled.div.attrs({ role: 'menuitem' })<{
  disabled?: boolean;
}>`
  padding: var(--spacing-m);
  font-size: 14px;
  user-select: none;
  border-radius: var(--radius-8);
  color: var(
    ${(p) =>
      p.disabled
        ? '--color-interface-content-lightest'
        : '--color-interface-secondary-normal'}
  );
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color var(--transition-normal);

  :hover {
    background-color: var(--color-interface-layout-lighter);
  }
`;

const SaveChangesOption: React.FC<any> = ({
  updateSavedQuery,
  selectedView,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const datasourceId = useDatasourceId();
  const dataPresentation = useDataPresentationParam();
  const { toggle } = useDropdown();
  const steps = useSelector(getQueryBuilderSteps);
  const selectedStepId = useSelector(getSelectedLayerId);
  const chartConfiguration = useSelector(getChartingOptions);
  const metricsDimensionConfiguration = useSelector(
    selectMetricsDimensionConfiguration,
  );
  const isLoadingRows = useSelector(selectIsLoadingRows);

  const disabled = !selectedView;
  const layerHelper = new LayerHelper(steps, selectedStepId);
  const activeSteps = layerHelper.activeLayers();
  const inactiveSteps = layerHelper.inactiveLayers();

  const handleSaveChanges = async () => {
    const { data } = await updateSavedQuery({
      datasourceId,
      seriesId: selectedView.seriesId,
      updatedSavedQuery: {
        existing: {
          _id: selectedView.id,
          _name: selectedView.name,
          _series: selectedView.seriesId,
          _saved: true,
          _categories: [],
        },
        chartConfiguration,
        activeSteps,
        inactiveSteps,
        metricsDimensionConfiguration,
      },
    });

    const updatedSeriesId = data.existing._series;
    const dataPresentationPath = dataPresentation ? `/${dataPresentation}` : '';
    const nextUrl = `/datasources/${datasourceId}/saved-queries/${updatedSeriesId}${dataPresentationPath}`;

    if (nextUrl === pathname) return;

    // Navigate within the next loop tick when RTK-Query cache is already updated.
    setTimeout(navigate, 0, nextUrl);
  };

  const handleClick = async () => {
    if (disabled) return;
    dispatch(reset());
    toggle();

    await handleSaveChanges();
    await invalidateSavedQueryTags();
  };

  /**
   * we invalidate cache to fire columns and rows requests
   * to avoid unstable cache behavior when a previous
   * ad-hoc rows request is still loading.
   */
  const invalidateSavedQueryTags = async () => {
    if (isLoadingRows) {
      await latestAdHocPromises.rows;
      dispatch(
        queryBuilderSavedQueriesApi.util.invalidateTags([
          GET_SAVED_QUERY_COLUMNS,
          GET_SAVED_QUERY_CACHED_ROWS,
        ]),
      );
    }
  };

  return (
    <SaveOption disabled={disabled} onClick={handleClick}>
      Save changes
    </SaveOption>
  );
};

const SaveQueryOption: React.FC<any> = ({ handleOpenSaveNew }) => {
  const dispatch = useAppDispatch();
  const { toggle } = useDropdown();

  const handleClick = () => {
    handleOpenSaveNew();
    dispatch(reset());
    toggle();
  };

  return (
    <>
      <SaveOption onClick={handleClick}>Save query as...</SaveOption>
    </>
  );
};

export const SaveButton = () => {
  const [isSaveNewOpen, handleOpenSaveNew] = useModal();
  const { data: selectedView } = useGetSavedQuery();
  const userActions = useSelector(getUserActions);
  const isDisabled = /cycle/i.test(useSelector(getRowsErrorMessage));
  const isChartingOptionsShown = useSelector(getShowChartingOptions);

  const [updateSavedQuery, { isLoading }] = useUpdateSavedQueryMutation();

  const isOnboardingShown = React.useMemo(
    () =>
      showOnboarding(
        userActions,
        ACTION_SAVE_QUERY,
        ONBOARDING_SAVE_QUERY_REQUIRED_ACTION as any,
      ),
    [userActions],
  );

  return (
    <>
      <Dropdown>
        <Dropdown.Trigger>
          <Button
            style={{ width: '100px' }}
            size="small"
            data-testid={SAVE_QUERY_BUTTON}
            disabled={isDisabled}
            icon={<TrevorIcon name="chevron-down" />}
          >
            <ButtonText isSavingInProgress={isLoading} />
            {isOnboardingShown && !isChartingOptionsShown && (
              <SaveQuerySuggestion />
            )}
          </Button>
        </Dropdown.Trigger>
        <SaveOptionsContainer top={27} left={-95}>
          <SaveChangesOption
            updateSavedQuery={updateSavedQuery}
            selectedView={selectedView}
          />
          <SaveQueryOption handleOpenSaveNew={handleOpenSaveNew} />
        </SaveOptionsContainer>
      </Dropdown>
      <SavedQueryNextSteps />
      {isSaveNewOpen && <SaveQueryModal />}
    </>
  );
};
